import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { setAppModal, setAppModalData } from '../../../redux/action/system'
import { ModalButtons, ModalButtonsItem, ModalButtonsItemDeny, ModalButtonsItemText, ModalContent, OrderInfo, OrderInfoBlock, OrderInfoItem, OrderMap, OrderTitle, OrderUnpaidContent, OrderUnpaidText, OrderUnpaidWrapper, OrderWrap, ProcessingModalWrapper } from './index.styled'
import { getNeededDateFormat } from '../../../helper'
import { addOrderProcessingTimeThunk, denyOrderThunk, removeOrderFromProcessingTimeModalDataThunk } from '../../../redux/thunk/order/addOrderProcessingTime'
import Currency from '../../Elements/currency'
import Loader from '../../Elements/loaders'
import colors from '../../../style/colors'
import { orderTypeOptionsStatic } from '../../../redux/reducer/order/constants'
import CustomModal from '../../Elements/modal/CustomModal'
import MainIcon from '../../../img/svg/main'
import { selectCompanyIndex, selectIsNeededZIndex, selectUsername } from '../../../redux/selector/system'
import Order from '../../Elements/modal/order/Order'
import KeyBindings from '../../Elements/keybindings'
import { selectSubscriptionData } from '@liefersoft/pos-ui-core'
import { convertMapImageUrl } from './helper'
import { logger } from '../../../logger/logger'
import { theme } from '../../../style/theme'
import { useWindowBreakPoint } from '../../../hooks/useWindowBreakPoint'
import ProcessingTimeOrderTabs from './tabs'

const EVENT_Z_INDEX = 998
const BUTTONS_COUNT = 6
const showDenyButtonPlatformTypeList = ['UBER_EAT_PLUGIN', 'WOLT_PLUGIN', 'ONLINE_SHOP', 'FOODORA_PLUGIN']
const showPlatformDenyModal = 'FOODORA_PLUGIN'

const ProcessingTimeOrderModal = React.memo(() => {
	const { t } = useTranslation()
	const processingTimeOrderModalShow = useSelector(store => store.system.modal?.processingTimeOrderModalShow)
	const isNeededZIndex = useSelector(selectIsNeededZIndex(EVENT_Z_INDEX))
	const order = useSelector(store => store.system.modalData?.processingTimeOrderModalData[0])
	const isProcessingConfirmOrder = useSelector(store => store.order.isProcessingConfirmOrder)
	const isProcessingDenyOrder = useSelector(store => store.order.isProcessingDenyOrder)
	const dispatch = useDispatch()
	const [activeButtonIndex, setActiveButtonIndex] = useState(2)
	const [imageLoaded, setImageLoaded] = useState(false)
	const [currentTab, setCurrentTab] = useState('MAP')
	const geoData = order?.customer?.geoData
	const timeButtons = useMemo(() => [15, 30, 45, 60, 70, 90], [])
	const showDenyButton = showDenyButtonPlatformTypeList.includes(order?.platformMetaData?.platformType)
	const companyIndex = useSelector(selectCompanyIndex)
	const payed = order?.payments?.filter(item => item?.method === order?.paymentMethod)[0]?.payed
	const { isDeactivated, willBeEmergencyBlocked } = useSelector(selectSubscriptionData)
	const isDisabled = isProcessingConfirmOrder || isProcessingDenyOrder
	const layoutDisabled = isDisabled ? 'disabled ' : ''
	const username = useSelector(selectUsername)
	const modalIsOpen = processingTimeOrderModalShow && (willBeEmergencyBlocked || !isDeactivated)
	const isMobile = useWindowBreakPoint(theme.point720)
	const isTabOrder = currentTab === 'ORDER'
	const showOrderData = !geoData || (isTabOrder && geoData)
	const layoutOrder = geoData ? 'with-tabs' : ''

	useEffect(() => {
		setImageLoaded(false)
	}, [])

	useEffect(() => {
		if (processingTimeOrderModalShow) {
			logger.info(`Opening processing time popup for confirming the order from platform orderId=${order.orderId} companyIndex=${companyIndex} path=${window.location.pathname}`)
		}
	}, [processingTimeOrderModalShow]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleConfirm = useCallback((processingTime) => {
		const data = { minutes: processingTime, printerMetaData: { userPrintRequest: { userName: username } } }
		dispatch(addOrderProcessingTimeThunk(order?.orderId, data))
	}, [dispatch, order?.orderId, username])

	const handleClose = useCallback(() => {
		dispatch(removeOrderFromProcessingTimeModalDataThunk(order?.orderId))
	}, [dispatch, order])

	const handleArrowUp = useCallback(() => {
		if ((activeButtonIndex - 1) < 0) {
			setActiveButtonIndex(BUTTONS_COUNT - 1)
		} else {
			setActiveButtonIndex(activeButtonIndex - 1)
		}
	}, [activeButtonIndex])

	const handleArrowDown = useCallback(() => {
		if ((activeButtonIndex + 1) >= BUTTONS_COUNT) {
			setActiveButtonIndex(0)
		} else {
			setActiveButtonIndex(activeButtonIndex + 1)
		}
	}, [activeButtonIndex])

	const handleEnter = useCallback(() => {
		handleConfirm(timeButtons[activeButtonIndex])
	}, [activeButtonIndex, handleConfirm, timeButtons])

	const handleDeny = useCallback(() => {
		if (order?.platformMetaData?.platformType === showPlatformDenyModal) {
			dispatch(setAppModal({ denyOrderModalShow: true }))
			dispatch(setAppModalData({ denyOrderModalData: [order] }))
			handleClose()
		} else {
			dispatch(denyOrderThunk(order?.orderId, 'Denied by user'))
		}
	}, [dispatch, order, handleClose])

	const handleClickTab = useCallback((value) => {
		setCurrentTab(value)
	}, [])

	return (
		<>
			<CustomModal isOpen={modalIsOpen}
									 close={handleClose}
									 id="platform-order-modal"
									 enableOutsideClick={false}
									 size="auto"
									 zIndex={EVENT_Z_INDEX}
									 title={`${t('Modal.change_order')} (${t(orderTypeOptionsStatic.find(t => t.value === order?.orderType)?.label)})`}>
				<ProcessingModalWrapper data-testid="processing-time-wrapper" className={geoData ? 'with-geodata' : ''}>
					<ModalContent>
						<OrderInfo>
							<OrderInfoBlock className="small">
								{!payed ?
									<OrderUnpaidWrapper>
										<OrderUnpaidContent>
											<MainIcon icon={order?.paymentMethod} />
											<OrderUnpaidText data-testid="price"><Currency>{order?.totalPrice}</Currency></OrderUnpaidText>
										</OrderUnpaidContent>
										<OrderUnpaidText className="small">{t('Modal.unpaid_order')}</OrderUnpaidText>
									</OrderUnpaidWrapper> :
									<OrderInfoItem>
										<MainIcon icon={order?.paymentMethod} />
										<p data-testid="price"><Currency>{order?.totalPrice}</Currency></p>
									</OrderInfoItem>}
							</OrderInfoBlock>
							<OrderInfoBlock className="small">
								<OrderInfoItem>
									<MainIcon icon="restaurant-name" />
									<p data-testid="platform">{order?.platformMetaData?.platformName}</p>
								</OrderInfoItem>
							</OrderInfoBlock>
							<OrderInfoBlock className="lf-col-2">
								<OrderInfoItem className="small">
									<MainIcon icon="time" />
									<p data-testid="time">{getNeededDateFormat(order?.orderDate, 'HH:mm') + ' UHR'}</p>
								</OrderInfoItem>
								<OrderInfoItem className="small">
									<MainIcon icon="date" />
									<p>{getNeededDateFormat(order?.orderDate)}</p>
								</OrderInfoItem>
								{geoData &&
									<OrderInfoItem className="small left">
										<MainIcon icon="route" />
										<p data-testid="address">{order?.customer?.street} {order?.customer?.streetNumber}, {order?.customer?.postalCode}</p>
										<p className={geoData?.distance > 0 ? '' : 'lf-hidden'} data-testid="distance">{(geoData?.distance / 1000).toFixed(2)} km</p>
									</OrderInfoItem>}
								{geoData?.duration > 0 &&
									<OrderInfoItem className="small">
										<MainIcon icon="time-left" />
										<p data-testid="duration">{Math.round(geoData?.duration / 60)} min</p>
									</OrderInfoItem>}
							</OrderInfoBlock>
						</OrderInfo>
						{!isMobile && geoData && <ProcessingTimeOrderTabs currentTab={currentTab} onClickTab={handleClickTab} />}
						{geoData && !isTabOrder && (
							<OrderMap>
								<img className={imageLoaded ? 'loaded' : ''} src={convertMapImageUrl(geoData?.image)} alt="Map" onLoad={() => setImageLoaded(true)} />
								{!imageLoaded && <Loader color={colors.blue} zoom={2} />}
							</OrderMap>
						)}
						{!isMobile && showOrderData && <OrderWrap className={layoutOrder}>
							<OrderTitle className={order?.orderDayCounter ? '' : 'lf-hidden'}>{'Bestellungsnr. ' + order?.orderDayCounter}</OrderTitle>
							<Order order={order} />
						</OrderWrap>}
					</ModalContent>
					<ModalButtons>
						{timeButtons?.map((time, index) =>
							<ModalButtonsItem key={time}
																className={(activeButtonIndex === index ? 'active ' : '') + layoutDisabled}
																onClick={!isDisabled ? () => handleConfirm(time) : () => {
																}}>
								<ModalButtonsItemText>{time} min</ModalButtonsItemText>
							</ModalButtonsItem>)}
						{showDenyButton &&
							<ModalButtonsItemDeny onClick={!isDisabled ? handleDeny : () => {
							}} className={layoutDisabled}>
								<MainIcon icon="cancel" fill={colors.white} />
								<ModalButtonsItemText className="regular">{t('Modal.deny_order')}</ModalButtonsItemText>
							</ModalButtonsItemDeny>}
					</ModalButtons>
					{isMobile && <OrderWrap className={layoutOrder}>
						<OrderTitle className={order?.orderDayCounter ? '' : 'lf-hidden'}>{'Bestellungsnr. ' + order?.orderDayCounter}</OrderTitle>
						<Order order={order} />
					</OrderWrap>}
				</ProcessingModalWrapper>
			</CustomModal>

			{(isNeededZIndex && !isDisabled) &&
				<KeyBindings enter={handleEnter} arrowUp={handleArrowUp} arrowDown={handleArrowDown} />}
		</>
	)
})

ProcessingTimeOrderModal.displayName = 'ProcessingTimeOrderModal'
export default ProcessingTimeOrderModal
